"use client"

import React, { ReactNode } from "react"
import { clsx } from "clsx"
import { SIGN_UP_URI } from "config/route-path"
import { FormattedMessage } from "react-intl"

import { Box, Button, H1, P } from "@bullseye/components"

import AnimatedBackground from "../components/AnimatedBackground"
import { NiceToMeetYou } from "../components/NiceToMeetYou"
import AnimatedImage from "./AnimatedImage"

const backgroundUrl =
  "data:image/svg+xml,%3Csvg width='1440' height='640' viewBox='0 0 1440 640' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M1181.85 -113.77C1218.34 -115.16 1256.16 -89.3033 1266.32 -56.0182L1461.82 584.504C1479.38 642.022 1409.37 671.774 1354.64 630.047L724.987 149.917L1229.56 1028.6C1241.65 1049.65 1241.92 1073.08 1228.57 1089.59C1221.48 1098.36 1207.63 1108.22 1185.23 1108.5C1161.46 1108.79 1140.83 1098.18 1127.79 1087.67C1082.7 1051.37 981.885 954.904 858.517 836.863C848.487 827.266 838.308 817.526 827.998 807.665C688.348 674.093 519.598 513.194 355.895 365.318C191.228 216.573 35.3322 84.3787 -79.2227 6.28082C-90.5499 -1.4415 -101.103 -8.37335 -110.896 -14.568C-21.8706 82.5722 114.661 242.474 235.453 429.594C428.759 729.05 594.305 1118.09 426.71 1426.59C412.055 1453.56 371.858 1459.58 336.927 1440.04C301.997 1420.49 285.56 1382.77 300.215 1355.8C440.573 1097.44 306.923 759.475 119.644 469.357C-65.7611 182.142 -291.458 -38.7136 -298.523 -45.5958C-303.949 -50.882 -311.242 -59.3172 -316.203 -70.6172C-321.357 -82.3579 -321.784 -92.1435 -321.254 -98.4449C-321.165 -99.4919 -321.036 -100.522 -320.893 -101.473C-318.149 -126.86 -307.302 -152.908 -279.325 -167.857C-250.894 -183.049 -216.484 -180.488 -188.205 -173.496C-135.077 -160.358 -72.7611 -124.075 -11.998 -82.6493C114.039 3.27671 278.046 143.016 442.934 291.961C608.785 441.775 779.243 604.322 918.795 737.799C922.442 741.287 926.065 744.753 929.663 748.194L474.238 -44.8956C438.266 -107.539 513.178 -152.116 575.088 -104.908L1282.31 434.364L1134.17 -50.9868C1124.01 -84.2719 1145.36 -112.381 1181.85 -113.77ZM-199.23 -58.4134C-199.334 -58.4191 -199.39 -58.4269 -199.39 -58.4269C-199.39 -58.4269 -199.34 -58.4257 -199.23 -58.4134Z' fill='%238B4FE3'/%3E%3C/svg%3E%0A"

type Props = {
  heroImage?: string
  title?: ReactNode
  subtitle?: ReactNode
}

const defaultHeroImage =
  "https://storage.googleapis.com/hank-app-assets/marketing/group-of-friends-hero.jpg"

const HeroSignUp = ({
  heroImage = defaultHeroImage,
  title,
  subtitle,
}: Props) => {
  const isDefaultHero = heroImage === defaultHeroImage
  return (
    <Box className="relative z-[1] flex items-center overflow-hidden bg-primary-900 px-4 py-10 text-white md:py-[120px]">
      <AnimatedBackground backgroundUrl={backgroundUrl} />
      <Box className="max-w-[992px]">
        <Box className="flex flex-col items-center g-16 md:flex-row md:g-24">
          <Box
            className={clsx("flex max-w-[400px] flex-1", {
              "max-w-[600px]": !isDefaultHero,
            })}
          >
            <H1 className="font-bold text-white">
              {title ? title : <FormattedMessage id="home.hero.title" />}
            </H1>
            <Box className="mb-10 mt-2">
              {subtitle ? (
                subtitle
              ) : (
                <P bold className="text-white" size="lg">
                  <FormattedMessage id="home.hero.subtitle" />
                </P>
              )}
            </Box>
            <Button height="lg" as="a" variant="highlight" href={SIGN_UP_URI}>
              <FormattedMessage id="general.getStarted.free" />
            </Button>
          </Box>
          <Box className="relative flex-1">
            {isDefaultHero && <NiceToMeetYou />}
            <AnimatedImage
              src={heroImage}
              alt="The secret to longevity? Friends."
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default HeroSignUp
